import { useEffect, useId, useRef, useState } from 'react';

interface SearchFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Enter in der Suche (z. B. bei einer Nummer die Zeile öffnen). */
  onEnter?: (value: string) => void;
  placeholder?: string;
  hint?: string;
  /** Trefferzahl: „12 von 300“, wird für Screenreader angesagt. */
  count?: { shown: number; total: number } | undefined;
  /** Wartet auf den Server (nur Server-Suche). */
  busy?: boolean;
  inputMode?: 'search' | 'text';
  /** Obergrenze der Eingabe (passt zur Grenze der Server-Suche). */
  maxLength?: number;
}

/**
 * Suchfeld für lange Listen: `type="search"`, Escape leert, Enter ruft `onEnter`, Trefferzahl in `aria-live`.
 * Die Filterung selbst macht die Seite (clientseitig mit `useDeferredValue` oder serverseitig mit `useDebouncedValue`).
 */
export function SearchField({
  label,
  value,
  onChange,
  onEnter,
  placeholder,
  hint,
  count,
  busy = false,
  inputMode = 'search',
  maxLength,
}: SearchFieldProps) {
  const id = useId();
  return (
    <div className="field search-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="search"
        enterKeyHint="search"
        inputMode={inputMode}
        autoComplete="off"
        spellCheck={false}
        maxLength={maxLength}
        placeholder={placeholder}
        value={value}
        aria-busy={busy}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && value !== '') {
            event.preventDefault();
            onChange('');
          } else if (event.key === 'Enter' && onEnter !== undefined) {
            event.preventDefault();
            onEnter(value);
          }
        }}
      />
      {hint !== undefined && <span className="field__hint">{hint}</span>}
      <span className="search-field__count" aria-live="polite">
        {count === undefined ? '' : `${String(count.shown)} von ${String(count.total)}`}
      </span>
    </div>
  );
}

/** Wert erst nach `delayMs` Ruhe übernehmen (Server-Suche: 250 ms). */
export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);
  return debounced;
}
